/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Download, Send, Smartphone } from 'lucide-react';
import api from '../services/api';
import {
  currentSubscription, permission, pushSupport, PushPermissionError, subscribeDevice, unsubscribeDevice,
} from '../services/push';
import { useInstallPrompt } from '../services/installPrompt';
import { useToast } from '../contexts/ToastContext';
import { formatPercent } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, Checkbox, Select, Skeleton, Spinner,
} from './ui';

const THRESHOLDS = [1, 2, 3, 5, 7, 10];
const MOVE_THRESHOLDS = [2, 3, 4, 5, 7, 10];

const KINDS = [
  { key: 'report', label: 'Rapport du jour', description: 'Dès que le rapport du matin est prêt, avec sa première ligne.' },
  { key: 'debate', label: 'Débats d’experts', description: 'Au début du débat quotidien et à la fin d’un débat lancé à la demande.' },
  { key: 'alerts', label: 'Alertes de marché', description: 'Quand le portefeuille varie fortement dans la journée (une alerte par sens et par jour).' },
  { key: 'moves', label: 'Pourquoi ça bouge ?', description: 'Quand une de vos lignes varie fortement en séance, avec la raison trouvée dans l’actualité du jour (titres Google News lus par un modèle économique de votre fournisseur d’IA).' },
  { key: 'earnings', label: 'Résultats des entreprises', description: 'Un briefing deux jours avant la publication des résultats d’une de vos lignes, puis l’analyse le soir même.' },
  { key: 'triggers', label: 'Déclencheurs du panel', description: 'Quand une valeur de la watchlist atteint le niveau fixé par le rapport du jour (vérifié toutes les 15 minutes en séance).' },
];

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

const DeviceCard = ({ config, onDevices }) => {
  const toast = useToast();
  const support = pushSupport();
  const [subscribed, setSubscribed] = useState(null);
  const [perm, setPerm] = useState(permission());
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    currentSubscription().then((sub) => { if (alive) setSubscribed(Boolean(sub)); }).catch(() => { if (alive) setSubscribed(false); });
    return () => { alive = false; };
  }, []);

  const enable = async () => {
    setError('');
    setBusy('enable');
    try {
      const { devices } = await subscribeDevice(config.public_key);
      setSubscribed(true);
      onDevices(devices);
      toast.success('Notifications activées sur cet appareil');
    } catch (err) {
      setPerm(permission());
      setError(err instanceof PushPermissionError
        ? 'Les notifications sont refusées pour ce site : autorisez-les dans les réglages du navigateur, puis réessayez.'
        : errorText(err, 'Impossible d’activer les notifications sur cet appareil.'));
    } finally {
      setBusy('');
    }
  };

  const disable = async () => {
    setError('');
    setBusy('disable');
    try {
      const { devices } = await unsubscribeDevice();
      setSubscribed(false);
      onDevices(devices);
      toast.success('Notifications désactivées sur cet appareil');
    } catch (err) {
      setError(errorText(err, 'Impossible de désactiver les notifications sur cet appareil.'));
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setError('');
    setBusy('test');
    try {
      const res = await api.post('/push/test');
      if (res.data?.sent > 0) toast.success('Notification de test envoyée', { description: `Vers ${plural(res.data.sent, 'appareil')}.` });
      else setError('Aucun appareil n’a reçu la notification : désactivez puis réactivez les notifications sur cet appareil.');
    } catch (err) {
      setError(errorText(err, 'Impossible d’envoyer la notification de test.'));
    } finally {
      setBusy('');
    }
  };

  let badge = <Badge tone="neutral" dot="pulse">Vérification…</Badge>;
  if (support !== 'ok') badge = <Badge tone="neutral">Non disponible</Badge>;
  else if (perm === 'denied') badge = <Badge tone="loss" dot>Bloquées</Badge>;
  else if (subscribed === true) badge = <Badge tone="gain" dot>Activées</Badge>;
  else if (subscribed === false) badge = <Badge tone="warn" dot>Désactivées</Badge>;

  return (
    <Card data-testid="push-device">
      <CardHeader icon={Smartphone} title="Cet appareil" description="Recevez les notifications même lorsque l’application est fermée." actions={badge} />
      {support === 'ios-install' && (
        <Banner tone="info">
          Sur iPhone et iPad, ajoutez d’abord PEA Wallet à l’écran d’accueil (Partager, puis « Sur l’écran d’accueil »), ouvrez l’application depuis son icône et revenez ici.
        </Banner>
      )}
      {support === 'unsupported' && (
        <Banner tone="info">Ce navigateur ne prend pas en charge les notifications push.</Banner>
      )}
      {support === 'ok' && subscribed === null && <Spinner label="Vérification de l’abonnement…" />}
      {support === 'ok' && subscribed !== null && (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-fg-muted">
            {subscribed
              ? 'Cet appareil reçoit les notifications choisies ci-dessous.'
              : perm === 'denied'
                ? 'Les notifications sont bloquées pour ce site : autorisez-les dans les réglages du navigateur, puis réessayez.'
                : 'Activez les notifications : le navigateur vous demandera votre accord.'}
            {config.devices > 0 && <span className="text-fg-subtle"> {plural(config.devices, 'appareil')} {config.devices > 1 ? 'abonnés' : 'abonné'} au total.</span>}
          </p>
          <div className="flex flex-wrap gap-3">
            {subscribed ? (
              <>
                <Button leadingIcon={Send} loading={busy === 'test'} disabled={Boolean(busy)} onClick={test}>Envoyer un test</Button>
                <Button variant="secondary" leadingIcon={BellOff} loading={busy === 'disable'} disabled={Boolean(busy)} onClick={disable}>Désactiver sur cet appareil</Button>
              </>
            ) : (
              <Button leadingIcon={BellRing} loading={busy === 'enable'} disabled={Boolean(busy)} onClick={enable}>Activer sur cet appareil</Button>
            )}
          </div>
        </div>
      )}
      {error && <Banner tone="loss" role="alert" className="mt-4">{error}</Banner>}
    </Card>
  );
};

const InstallCard = () => {
  const { installed, canInstall, install } = useInstallPrompt();
  const toast = useToast();
  const onInstall = async () => {
    if (await install()) toast.success('PEA Wallet est installée');
  };
  return (
    <Card data-testid="pwa-install">
      <CardHeader
        icon={Download}
        title="Application"
        description="Installez PEA Wallet pour l’ouvrir comme une application, en plein écran."
        actions={installed ? <Badge tone="gain" dot>Installée</Badge> : null}
      />
      {installed && <p className="text-sm text-fg-muted">Vous utilisez l’application installée.</p>}
      {!installed && canInstall && <Button leadingIcon={Download} onClick={onInstall}>Installer PEA Wallet</Button>}
      {!installed && !canInstall && (
        <p className="text-sm leading-relaxed text-fg-muted">
          Utilisez « Installer l’application » dans le menu du navigateur, ou « Sur l’écran d’accueil » dans le menu Partager de Safari.
        </p>
      )}
    </Card>
  );
};

const PreferencesCard = ({ preferences, onSaved }) => {
  const toast = useToast();
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const save = async (patch, key) => {
    setError('');
    setSaving(key);
    try {
      const res = await api.put('/push/preferences', { ...preferences, ...patch });
      onSaved(res.data);
      toast.success('Préférences enregistrées');
    } catch (err) {
      setError(errorText(err, 'Impossible d’enregistrer les préférences.'));
    } finally {
      setSaving('');
    }
  };

  return (
    <Card>
      <CardHeader icon={Bell} title="Que voulez-vous recevoir ?" description="Valable pour tous vos appareils abonnés." />
      <ul className="divide-y divide-line">
        {KINDS.map((kind) => {
          const id = `push-pref-${kind.key}`;
          return (
            <li key={kind.key} className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
              <label htmlFor={id} className="min-w-0 cursor-pointer">
                <span className="block text-sm font-medium text-fg">{kind.label}</span>
                <span className="mt-0.5 block text-sm leading-relaxed text-fg-muted">{kind.description}</span>
              </label>
              <Checkbox
                switch
                id={id}
                className="mt-0.5"
                checked={Boolean(preferences[kind.key])}
                disabled={Boolean(saving)}
                aria-busy={saving === kind.key || undefined}
                onChange={(e) => save({ [kind.key]: e.target.checked }, kind.key)}
              />
            </li>
          );
        })}
      </ul>
      <div className="mt-5 flex flex-col gap-2 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
        <label htmlFor="push-threshold" className="text-sm text-fg-body">
          Seuil d’alerte
          <span className="mt-0.5 block text-xs text-fg-subtle">Variation du portefeuille depuis la clôture de la veille.</span>
        </label>
        <Select
          id="push-threshold"
          className="sm:w-40"
          value={String(preferences.alert_threshold)}
          disabled={!preferences.alerts || Boolean(saving)}
          onChange={(e) => save({ alert_threshold: Number(e.target.value) }, 'threshold')}
        >
          {!THRESHOLDS.includes(preferences.alert_threshold) && (
            <option value={String(preferences.alert_threshold)}>{formatPercent(preferences.alert_threshold, { sign: false, decimals: 1 })}</option>
          )}
          {THRESHOLDS.map((t) => <option key={t} value={String(t)}>{formatPercent(t, { sign: false, decimals: 0 })}</option>)}
        </Select>
      </div>
      <div className="mt-5 flex flex-col gap-2 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
        <label htmlFor="push-move-threshold" className="text-sm text-fg-body">
          Seuil « Pourquoi ça bouge ? »
          <span className="mt-0.5 block text-xs text-fg-subtle">Variation d’une ligne depuis la clôture de la veille.</span>
        </label>
        <Select
          id="push-move-threshold"
          className="sm:w-40"
          value={String(preferences.move_threshold ?? 4)}
          disabled={!preferences.moves || Boolean(saving)}
          onChange={(e) => save({ move_threshold: Number(e.target.value) }, 'move-threshold')}
        >
          {!MOVE_THRESHOLDS.includes(preferences.move_threshold ?? 4) && (
            <option value={String(preferences.move_threshold)}>{formatPercent(preferences.move_threshold, { sign: false, decimals: 1 })}</option>
          )}
          {MOVE_THRESHOLDS.map((t) => <option key={t} value={String(t)}>{formatPercent(t, { sign: false, decimals: 0 })}</option>)}
        </Select>
      </div>
      {error && <Banner tone="loss" role="alert" className="mt-4">{error}</Banner>}
    </Card>
  );
};

const NotificationsSettings = () => {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/push/config');
      setConfig(res.data);
    } catch (err) {
      setError(errorText(err, 'Impossible de charger les notifications.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return <Banner tone="loss" role="alert" action={<Button size="sm" variant="secondary" onClick={load}>Réessayer</Button>}>{error}</Banner>;
  }
  if (!config) {
    return (
      <div className="space-y-6" role="status">
        <span className="sr-only">Chargement des notifications…</span>
        <Card aria-hidden="true"><Skeleton className="h-24 w-full" /></Card>
        <Card aria-hidden="true"><Skeleton className="h-40 w-full" /></Card>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <DeviceCard config={config} onDevices={(devices) => { if (devices !== undefined) setConfig((c) => ({ ...c, devices })); }} />
      <PreferencesCard preferences={config.preferences} onSaved={(preferences) => setConfig((c) => ({ ...c, preferences }))} />
      <InstallCard />
    </div>
  );
};

export default NotificationsSettings;
